import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { ILedgerSettingsService } from './ILedgerSettingsService';
import type { RecordTaxChangeInput } from './requests';
import type {
    GetLedgerSettingsResponse,
    RecordTaxChangeResponse,
    TaxRateSetResponse,
    TaxSettingsHistoryResponse,
} from './responses';
import { SettingsEndpoints } from './settingsEndpoints';
import { deriveOverrides } from './taxRegimes';
import {
    toRecordTaxChangeWire,
    toTaxRateSet,
    type TaxRateSetWire,
    type TaxSettingsHistoryWire,
} from './taxSettingsMapping';

type LedgerSettingsServiceOptions = {
    baseUrl: string;
};

/** An unmapped regime is a contract drift, not a value to guess at. */
const UNMAPPED_REGIME: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message:
            'The server returned a tax regime this client does not recognise.',
        code: 'UNMAPPED_REGIME',
    },
};

/**
 * The live tax-settings client.
 *
 * Every method maps at the boundary — decimal percent to basis points, wire
 * regime names to this client's — via `taxSettingsMapping`, so nothing
 * downstream sees a wire shape.
 */
export class LedgerSettingsService implements ILedgerSettingsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: LedgerSettingsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getCurrentTaxSettings(): Promise<
        ApiResponse<TaxRateSetResponse>
    > {
        const result = await makeApiRequest<TaxRateSetWire>(
            this.client.get(SettingsEndpoints.current()),
        );
        return mapSet(result);
    }

    public async getTaxSettingsOnDate(
        date: string,
    ): Promise<ApiResponse<TaxRateSetResponse>> {
        const result = await makeApiRequest<TaxRateSetWire>(
            this.client.get(SettingsEndpoints.onDate(date)),
        );
        return mapSet(result);
    }

    public async getTaxSettingsHistory(): Promise<
        ApiResponse<TaxSettingsHistoryResponse>
    > {
        const result = await makeApiRequest<TaxSettingsHistoryWire>(
            this.client.get(SettingsEndpoints.history()),
        );
        if (!result.success) {
            return result;
        }
        const sets = [];
        for (const wire of result.response.sets ?? []) {
            const set = toTaxRateSet(wire);
            if (set === null) {
                return UNMAPPED_REGIME;
            }
            sets.push(set);
        }
        return { success: true, response: { sets } };
    }

    /**
     * Record a change.
     *
     * **Both 200 and 201 are success**: the server returns 201 with a
     * `Location` header on a first create and 200 on an idempotent replay of
     * the same id with identical content. `makeApiRequest` already treats both
     * as success, so the status is re-read here only to report which happened.
     */
    public async recordTaxChange(
        body: RecordTaxChangeInput,
    ): Promise<ApiResponse<RecordTaxChangeResponse>> {
        const httpPromise = this.client.post(
            SettingsEndpoints.recordChange(),
            toRecordTaxChangeWire(body),
        );

        // Read the status off the same promise makeApiRequest consumes, so the
        // created/replayed distinction survives the envelope. A 4xx rejects,
        // and makeApiRequest turns that into a coded error — this handler only
        // exists so the rejection is not also unhandled here.
        const statusPromise = httpPromise.then(
            (response) => response.status,
            () => null,
        );

        const result = await makeApiRequest<TaxRateSetWire>(httpPromise);
        const status = await statusPromise;
        if (!result.success) {
            return result;
        }
        const set = toTaxRateSet(result.response);
        if (set === null) {
            return UNMAPPED_REGIME;
        }
        return {
            success: true,
            response: { set, created: status === 201 },
        };
    }

    /**
     * The rates in force today, flattened to the pre-BOS-90 shape for the
     * dividends screens. A 404 propagates: "no rates in force" is a real answer
     * and those screens already handle absent settings.
     */
    public async getLedgerSettings(): Promise<
        ApiResponse<GetLedgerSettingsResponse>
    > {
        const result = await this.getCurrentTaxSettings();
        if (!result.success) {
            return result;
        }
        const set = result.response;
        return {
            success: true,
            response: {
                settings: {
                    regime: set.regime,
                    rates: set.rates,
                    // Derived here exactly as the old server did: by comparing
                    // the stored rates against the regime's figures. The
                    // versioned API has no `overrides` field.
                    overrides: deriveOverrides(set.regime, set.rates),
                    updatedAt: set.createdAt,
                },
            },
        };
    }
}

/** Shared success-path mapping for the two single-set reads. */
// (declared below the class so the class body reads top-down)
const mapSet = (
    result: ApiResponse<TaxRateSetWire>,
): ApiResponse<TaxRateSetResponse> => {
    if (!result.success) {
        return result;
    }
    const set = toTaxRateSet(result.response);
    return set === null ? UNMAPPED_REGIME : { success: true, response: set };
};
